/* lib/api/drfErrors.js — один разбор тела ошибки DRF: для unwrap (throw-контракт экранов,
   e.detail / e.field) и для слоя таблиц (errorMessage очереди правок).

   Формы, которые присылает DRF:
   · {поле: ["текст", …]} — сериализатор (is_valid): строки завёрнуты в список;
   · {поле: "текст"} — ValidationError, поднятая во view или в save() (вне is_valid): DRF
     строку в список НЕ заворачивает ({"ttk_id": "ТТК #5 не найдена…"});
   · ["текст", …] — ValidationError("…") во view: список верхнего уровня;
   · вложенные сериализаторы — {name: {ru: [..]}}, {rows: [{}, {brutto_g: [..]}]}.
   Строка и вложенная структура — сообщение только в ответе валидации (400): у иных ответов
   строки тела — данные (state, title, committed_kind…), список словарей — объекты
   (conflicts: [{ttk_id, title}]). */

/* Служебные ключи тела ошибки — не текст для человека. */
export const META_KEYS = new Set(['code', 'status', 'status_code']);

/* Информативные ключи тел ошибок (unwrap прокидывает их в err как есть) — числа, имена,
   объекты: used_in — [{id, title}], field — имя поля. Не текст ошибки. */
export const INFO_KEYS = new Set(['usages_count', 'rows_count', 'used_in', 'count', 'field',
  'committed_version_n', 'committed_id', 'committed_kind']);

/* Ключи без поля формы: общая ошибка сериализатора. */
const NON_FIELD_KEYS = new Set(['non_field_errors', 'message']);

/** Первое сообщение в любой форме DRF: строка, список, словарь — на любой глубине.
    `message`/`detail` словаря — раньше его полей. HTML (страница прокси) — не текст. */
export function firstMessage(x, depth = 0) {
  if (x == null || depth > 8) return null;
  if (typeof x === 'string') {
    const t = x.trim();
    return t && !t.startsWith('<') ? t : null;
  }
  if (Array.isArray(x)) {
    for (const item of x) {
      const m = firstMessage(item, depth + 1);
      if (m) return m;
    }
    return null;
  }
  if (typeof x === 'object') {
    for (const k of ['message', 'detail']) {
      const m = firstMessage(x[k], depth + 1);
      if (m) return m;
    }
    for (const [k, v] of Object.entries(x)) {
      if (k === 'message' || k === 'detail' || META_KEYS.has(k)) continue;
      const m = firstMessage(v, depth + 1);
      if (m) return m;
    }
  }
  return null;
}

/** Ошибка поля из тела DRF без detail/error: `{ message, field }` или null.
    field — ключ верхнего уровня (у non_field_errors и списка верхнего уровня поля нет).
    Список строк у поля — сообщение при любом коде ответа и под любым ключом (поле
    сериализатора может зваться status или code). Строка и вложенная структура — только в
    ответе валидации: код 400 или неизвестен (ошибку собрал вызывающий без кода); служебные
    и информативные ключи тут не текст. Первое поле с сообщением — по порядку ключей. */
export function drfFieldError(data, status) {
  if (data == null || typeof data !== 'object') return null;
  if (Array.isArray(data)) {
    const m = firstMessage(data);
    return m ? { message: m, field: undefined } : null;
  }
  const loose = status == null || status === 400;
  for (const [k, v] of Object.entries(data)) {
    if (k === 'detail' || k === 'error' || v == null) continue;
    const listOfText = Array.isArray(v) && typeof v[0] === 'string';
    if (!listOfText) {
      if (!loose || META_KEYS.has(k) || INFO_KEYS.has(k)) continue;
      if (typeof v !== 'string' && typeof v !== 'object') continue;
    }
    const m = firstMessage(v);
    if (m) return { message: m, field: NON_FIELD_KEYS.has(k) ? undefined : k };
  }
  return null;
}
